/**
 * ReorderTopicsModal — atomic sibling reorder for topic trees.
 *
 * One component reused at every depth:
 *   - Function header calls it with `parentTopicId = null` → reorders depth-0
 *   - Any topic's menu calls it with `parentTopicId = <that topic's id>` →
 *     reorders that topic's direct children
 *
 * Flow: Edit (type new positions) → Preview (see the resolved order + shifts)
 * → Confirm Save (one atomic backend call).
 *
 * Reorder algorithm (Rule A — insert, deterministic single pass):
 *   1. Split rows into "pinned" (admin changed the Position input) and
 *      "unpinned" (kept original relative order).
 *   2. Sort pinned by target ASC. Tie-break by original position ASC
 *      (lower original wins; loser gets bumped to next free slot).
 *   3. Walk slot 1..N: place pinned at their targets first; fill remaining
 *      slots with unpinned in original order.
 *   4. Send the resolved final ordering to the backend as `ordered_ids`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Search, ArrowDownAZ, RotateCcw, ChevronUp, ChevronDown,
  MoreVertical, ArrowUpFromLine, ArrowDownFromLine,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Resolve the final order from the admin's Position inputs.
 * @param {Array<{id, name, originalPosition, newPosition|null}>} rows
 * @returns {{ finalOrder: Array<{id, name, oldPos, newPos, delta}>, conflicts: string[] }}
 */
function computeFinalOrder(rows) {
  const N = rows.length;
  const conflicts = [];

  // Split into pinned (changed) vs unpinned (kept as-is)
  const pinned = [];
  const unpinned = [];
  rows.forEach((r) => {
    const target = r.newPosition;
    if (target !== null && target !== undefined && target !== r.originalPosition) {
      pinned.push({ ...r, target: Math.max(1, Math.min(N, target)) });
    } else {
      unpinned.push(r);
    }
  });

  // Sort pinned by target ASC, tie-break by original ASC
  pinned.sort((a, b) => a.target - b.target || a.originalPosition - b.originalPosition);

  // Build result slot-by-slot
  const result = new Array(N).fill(null);
  const unpinnedQueue = [...unpinned];

  for (const p of pinned) {
    let slot = p.target - 1; // 0-indexed
    // If target slot is taken, walk right to next free slot
    while (slot < N && result[slot] !== null) slot++;
    if (slot >= N) slot = result.findIndex((x) => x === null);
    if (result[p.target - 1] !== null && slot !== p.target - 1) {
      conflicts.push(`${p.name}: requested #${p.target} but placed at #${slot + 1} (slot taken)`);
    }
    result[slot] = p;
  }

  for (let i = 0; i < N; i++) {
    if (result[i] === null && unpinnedQueue.length > 0) {
      result[i] = unpinnedQueue.shift();
    }
  }

  return {
    finalOrder: result.map((r, idx) => ({
      id: r.id,
      name: r.name,
      oldPos: r.originalPosition,
      newPos: idx + 1,
      delta: idx + 1 - r.originalPosition,
    })),
    conflicts,
  };
}


const ReorderTopicsModal = ({
  open,
  onClose,
  functionId,
  parentTopicId,     // null for depth-0
  siblings,          // Array<{id, name}> in CURRENT order
  scopeLabel,        // e.g. "F4 · Motor · Parent Topics" or "Main Engine · Children"
  onSaved,
}) => {
  // Edit state — one input value per row
  const [rowInputs, setRowInputs] = useState({});
  const [phase, setPhase] = useState('edit'); // "edit" | "preview"
  const [previewOrder, setPreviewOrder] = useState([]);
  const [previewConflicts, setPreviewConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [rowMenuOpen, setRowMenuOpen] = useState(null);

  // Reset state whenever the modal is re-opened with new siblings
  useEffect(() => {
    if (open) {
      const init = {};
      siblings.forEach((s, i) => { init[s.id] = String(i + 1); });
      setRowInputs(init);
      setPhase('edit');
      setPreviewOrder([]);
      setPreviewConflicts([]);
      setSearch('');
    }
  }, [open, siblings]);

  const rows = useMemo(() => siblings.map((s, i) => ({
    id: s.id,
    name: s.name,
    originalPosition: i + 1,
  })), [siblings]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [rows, search]);

  const changedCount = useMemo(() => {
    let n = 0;
    rows.forEach((r) => {
      const val = parseInt(rowInputs[r.id], 10);
      if (Number.isFinite(val) && val !== r.originalPosition) n++;
    });
    return n;
  }, [rows, rowInputs]);

  const setPos = (id, value) => {
    setRowInputs((prev) => ({ ...prev, [id]: value }));
  };

  const handleReset = () => {
    const init = {};
    rows.forEach((r, i) => { init[r.id] = String(i + 1); });
    setRowInputs(init);
    toast.info('Reverted all changes');
  };

  const handleSortAlpha = () => {
    const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name));
    const init = {};
    sorted.forEach((r, i) => { init[r.id] = String(i + 1); });
    setRowInputs(init);
    toast.success('Ordered A → Z');
  };

  const handleMoveToTop = (id) => {
    setPos(id, '1');
    setRowMenuOpen(null);
  };

  const handleMoveToBottom = (id) => {
    setPos(id, String(rows.length));
    setRowMenuOpen(null);
  };

  const handlePreview = () => {
    // Build resolved rows using computeFinalOrder
    const parsed = rows.map((r) => {
      const raw = rowInputs[r.id];
      const n = parseInt(raw, 10);
      return {
        id: r.id,
        name: r.name,
        originalPosition: r.originalPosition,
        newPosition: Number.isFinite(n) ? n : r.originalPosition,
      };
    });
    const { finalOrder, conflicts } = computeFinalOrder(parsed);
    setPreviewOrder(finalOrder);
    setPreviewConflicts(conflicts);
    setPhase('preview');
  };

  const handleConfirmSave = async () => {
    if (!previewOrder.length) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/topics/reorder`,
        {
          function_id: functionId,
          parent_topic_id: parentTopicId,
          ordered_ids: previewOrder.map((r) => r.id),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const moved = previewOrder.filter((r) => r.delta !== 0).length;
      toast.success(`Reordered ${moved} topic${moved === 1 ? '' : 's'}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  // ---------------- render ----------------

  const isEdit = phase === 'edit';
  const N = rows.length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="reorder-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="text-base">
            {isEdit ? 'Reorder Topics' : 'Preview Order'}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">{scopeLabel}</p>
        </DialogHeader>

        {isEdit ? (
          <>
            {/* Search + utilities */}
            <div className="px-6 py-3 border-b space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  data-testid="reorder-search"
                  className="pl-8 h-8 text-sm"
                  placeholder="Search inside this list..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSortAlpha}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border text-slate-700 hover:bg-slate-50"
                    data-testid="reorder-sort-alpha"
                  >
                    <ArrowDownAZ className="w-3.5 h-3.5" /> A → Z
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border text-slate-700 hover:bg-slate-50"
                    data-testid="reorder-reset"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Reset
                  </button>
                </div>
                <span className="text-slate-500">{N} topics</span>
              </div>
            </div>

            {/* Row list */}
            <div className="overflow-y-auto flex-1 px-6 py-2">
              <div className="space-y-1">
                {filteredRows.map((r) => {
                  const val = rowInputs[r.id] ?? String(r.originalPosition);
                  const changed = parseInt(val, 10) !== r.originalPosition;
                  return (
                    <div
                      key={r.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded border ${changed ? 'border-indigo-200 bg-indigo-50/40' : 'border-transparent hover:bg-slate-50'}`}
                      data-testid={`reorder-row-${r.id}`}
                    >
                      <span className="text-[11px] text-slate-400 w-6 text-right">
                        #{r.originalPosition}
                      </span>
                      <span className="flex-1 text-sm text-slate-800 truncate" title={r.name}>
                        {r.name}
                      </span>
                      <Input
                        data-testid={`reorder-pos-${r.id}`}
                        className="w-16 h-7 text-sm text-center"
                        value={val}
                        onChange={(e) => setPos(r.id, e.target.value.replace(/[^\d]/g, ''))}
                        onBlur={(e) => {
                          const n = parseInt(e.target.value, 10);
                          if (!Number.isFinite(n) || n < 1) setPos(r.id, String(r.originalPosition));
                          else if (n > N) setPos(r.id, String(N));
                        }}
                      />
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setRowMenuOpen(rowMenuOpen === r.id ? null : r.id)}
                          className="p-1 rounded hover:bg-slate-100 text-slate-500"
                          data-testid={`reorder-row-menu-${r.id}`}
                          aria-label="Row shortcuts"
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                        {rowMenuOpen === r.id && (
                          <div className="absolute right-0 top-full mt-1 bg-white border rounded shadow-lg z-10 min-w-[140px] py-1 text-xs">
                            <button
                              type="button"
                              onClick={() => handleMoveToTop(r.id)}
                              className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2"
                            >
                              <ArrowUpFromLine className="w-3 h-3" /> Move to top
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveToBottom(r.id)}
                              className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2"
                            >
                              <ArrowDownFromLine className="w-3 h-3" /> Move to bottom
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {filteredRows.length === 0 && (
                  <p className="text-center text-sm text-slate-400 py-8">No topics match "{search}"</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t flex items-center justify-between bg-slate-50">
              <span className="text-xs text-slate-600" data-testid="reorder-change-count">
                {changedCount === 0 ? 'No changes yet' : `${changedCount} change${changedCount === 1 ? '' : 's'} pending`}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={onClose} data-testid="reorder-cancel">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handlePreview}
                  disabled={changedCount === 0}
                  data-testid="reorder-preview-btn"
                  className="bg-[#6366F1] hover:bg-[#4F46E5] text-white"
                >
                  Preview →
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* PREVIEW PHASE */}
            <div className="overflow-y-auto flex-1 px-6 py-3">
              <p className="text-xs text-slate-500 mb-2">
                Final order — {previewOrder.filter((r) => r.delta !== 0).length} topic{previewOrder.filter((r) => r.delta !== 0).length === 1 ? '' : 's'} will move.
                Rule A: inserting a topic shifts neighbours to make room.
              </p>
              {previewConflicts.length > 0 && (
                <div className="mb-3 p-2 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800 space-y-0.5">
                  <p className="font-medium">Auto-resolved conflicts:</p>
                  {previewConflicts.map((c, i) => <p key={i}>· {c}</p>)}
                </div>
              )}
              <div className="space-y-1" data-testid="reorder-preview-list">
                {previewOrder.map((r) => {
                  const delta = r.delta;
                  const moved = delta !== 0;
                  return (
                    <div
                      key={r.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm ${moved ? 'bg-indigo-50' : 'bg-white'}`}
                      data-testid={`reorder-preview-row-${r.id}`}
                    >
                      <span className="text-[11px] text-slate-500 w-8 text-right font-mono">
                        #{r.newPos}
                      </span>
                      <span className="flex-1 truncate text-slate-800" title={r.name}>
                        {r.name}
                      </span>
                      {moved && (
                        <span className={`inline-flex items-center gap-0.5 text-[11px] ${delta < 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                          was #{r.oldPos}
                          {delta < 0 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {Math.abs(delta)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Preview footer */}
            <div className="px-6 py-3 border-t flex items-center justify-end gap-2 bg-slate-50">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPhase('edit')}
                disabled={saving}
                data-testid="reorder-back-btn"
              >
                ← Back to edit
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmSave}
                disabled={saving}
                data-testid="reorder-confirm-btn"
                className="bg-[#6366F1] hover:bg-[#4F46E5] text-white"
              >
                {saving ? 'Saving...' : 'Confirm Save'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Named export for testing the pure algorithm
export { computeFinalOrder };
export default ReorderTopicsModal;
